import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, AlertTriangle, Activity, Heart, Pill, RefreshCw } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const SEV_COLORS = {
    low: "bg-green-100 text-green-700",
    moderate: "bg-amber-100 text-amber-700",
    high: "bg-red-100 text-red-700",
};

/**
 * AI Valuation Report panel — lives in the patient dashboard sidebar.
 * Calls /api/ai/valuation-report to ask Gemini 3 Flash to interpret the latest scan.
 */
export function AIValuationReport({ canGenerate }) {
    const { t } = useTranslation();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/ai/valuation-reports");
            setReports(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load reports");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const generate = async () => {
        setGenerating(true);
        try {
            const { data } = await api.post("/ai/valuation-report", {});
            setReports((r) => [data, ...r]);
            toast.success("AI Valuation Report ready");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || t("dashboard.ai.noScanFirst"));
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div className="space-y-5" data-testid="tab-ai-report">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="font-display text-2xl font-semibold flex items-center gap-2">
                        <Sparkles className="w-6 h-6 text-[var(--medx-orange)]" />
                        {t("dashboard.ai.title")}
                    </h2>
                    <p className="text-sm text-gray-600 max-w-2xl mt-1">{t("dashboard.ai.subtitle")}</p>
                </div>
                <button
                    onClick={generate}
                    disabled={generating || !canGenerate}
                    className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                    data-testid="ai-generate-btn"
                >
                    {generating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {generating ? t("dashboard.ai.generating") : t("dashboard.ai.generate")}
                </button>
            </div>

            <div className="card-soft p-4 bg-amber-50/60 border-amber-100 flex gap-3 items-start" data-testid="ai-disclaimer-banner">
                <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900">{t("dashboard.ai.disclaimer")}</div>
            </div>

            {loading ? (
                <div className="text-center py-12 text-gray-500">{t("common.loading")}</div>
            ) : reports.length === 0 ? (
                <div className="card-soft p-12 text-center">
                    <Sparkles className="w-10 h-10 mx-auto text-[var(--medx-orange)] mb-3" />
                    <div className="font-display text-lg font-semibold mb-1">No AI reports yet</div>
                    <p className="text-sm text-gray-600 max-w-md mx-auto">
                        Take a kiosk scan, then click <em>Generate report</em> to get personalised insights from the AI.
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {reports.map((r) => {
                        const rep = r.report || {};
                        return (
                            <div key={r.id} className="card-soft p-6" data-testid={`ai-report-${r.id}`}>
                                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                                    <div className="text-[11px] font-mono tracking-[0.18em] text-[var(--medx-orange)]">
                                        AI VALUATION · {new Date(r.created_at).toLocaleString()}
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <div className="text-xs font-mono text-gray-500 mb-1">{t("dashboard.ai.summary").toUpperCase()}</div>
                                    <p className="text-gray-800 leading-relaxed">{rep.summary}</p>
                                </div>

                                {rep.flags?.length > 0 && (
                                    <div className="mb-4">
                                        <div className="text-xs font-mono text-gray-500 mb-2 flex items-center gap-1">
                                            <Activity className="w-3 h-3" /> {t("dashboard.ai.flags").toUpperCase()}
                                        </div>
                                        <div className="space-y-2">
                                            {rep.flags.map((f, idx) => (
                                                <div key={idx} className="flex items-start gap-3 text-sm">
                                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono shrink-0 ${SEV_COLORS[f.severity] || SEV_COLORS.low}`}>
                                                        {f.severity}
                                                    </span>
                                                    <div><strong>{f.vital}</strong> — {f.finding}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {rep.recommendations?.length > 0 && (
                                    <div className="mb-4">
                                        <div className="text-xs font-mono text-gray-500 mb-2 flex items-center gap-1">
                                            <Heart className="w-3 h-3" /> {t("dashboard.ai.recommendations").toUpperCase()}
                                        </div>
                                        <ul className="space-y-1.5">
                                            {rep.recommendations.map((line, i) => (
                                                <li key={i} className="text-sm text-gray-700 flex gap-2">
                                                    <span className="text-[var(--medx-red)] mt-1.5 w-1.5 h-1.5 rounded-full bg-current shrink-0" />
                                                    {line}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {rep.lifestyle_tips?.length > 0 && (
                                    <div className="mb-4">
                                        <div className="text-xs font-mono text-gray-500 mb-2">{t("dashboard.ai.lifestyle").toUpperCase()}</div>
                                        <ul className="space-y-1.5">
                                            {rep.lifestyle_tips.map((line, i) => (
                                                <li key={i} className="text-sm text-gray-700 flex gap-2">
                                                    <span className="text-[var(--medx-orange)] mt-1.5 w-1.5 h-1.5 rounded-full bg-current shrink-0" />
                                                    {line}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {rep.metric_explanations?.length > 0 && (
                                    <div className="mb-4">
                                        <div className="text-xs font-mono text-gray-500 mb-2">30+ METRICS — WHAT THEY MEAN</div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {rep.metric_explanations.map((m, i) => (
                                                <div key={i} className="bg-white border border-gray-100 rounded-lg p-3" data-testid={`ai-metric-${i}`}>
                                                    <div className="flex items-center justify-between gap-2 mb-1">
                                                        <strong className="text-sm text-gray-900 capitalize">{(m.metric || "").replace(/_/g, " ")}</strong>
                                                        <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${
                                                            m.status === "high" ? "bg-red-50 text-red-700" :
                                                            m.status === "elevated" ? "bg-amber-50 text-amber-700" :
                                                            m.status === "low" ? "bg-sky-50 text-sky-700" :
                                                            "bg-emerald-50 text-emerald-700"
                                                        }`}>
                                                            {(m.status || "normal").toUpperCase()}
                                                        </span>
                                                    </div>
                                                    <div className="text-xs text-gray-500 font-mono mb-1">{m.value}</div>
                                                    <div className="text-xs text-gray-700 leading-snug">{m.what_it_means}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {rep.dietary_recommendations?.length > 0 && (
                                    <div className="mb-4">
                                        <div className="text-xs font-mono text-gray-500 mb-2">DIETARY RECOMMENDATIONS</div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {rep.dietary_recommendations.map((d, i) => (
                                                <div key={i} className="bg-emerald-50/60 border border-emerald-100 rounded-lg p-3" data-testid={`ai-diet-${i}`}>
                                                    <strong className="text-sm text-gray-900">{d.title}</strong>
                                                    <div className="text-xs text-gray-700 mt-0.5 leading-snug">{d.detail}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {rep.home_remedies?.length > 0 && (
                                    <div className="mb-4">
                                        <div className="text-xs font-mono text-gray-500 mb-2">HELPFUL HOME REMEDIES</div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {rep.home_remedies.map((d, i) => (
                                                <div key={i} className="bg-blue-50/60 border border-blue-100 rounded-lg p-3" data-testid={`ai-remedy-${i}`}>
                                                    <strong className="text-sm text-gray-900">{d.title}</strong>
                                                    <div className="text-xs text-gray-700 mt-0.5 leading-snug">{d.detail}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {rep.suggested_otc?.length > 0 && (
                                    <div className="mb-4">
                                        <div className="text-xs font-mono text-gray-500 mb-2 flex items-center gap-1">
                                            <Pill className="w-3 h-3" /> {t("dashboard.ai.otc").toUpperCase()}
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {rep.suggested_otc.map((o, i) => (
                                                <div key={i} className="text-sm bg-orange-50/60 border border-orange-100 rounded-lg p-3">
                                                    <strong className="text-gray-900">{o.name}</strong>
                                                    <div className="text-xs text-gray-600">{o.purpose}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="text-xs text-amber-900 bg-amber-50 border-2 border-amber-200 rounded-lg p-4 mt-2 font-semibold" data-testid={`ai-disclaimer-${rep.id}`}>
                                    ⚠ IMPORTANT: {rep.disclaimer}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
